import { memo, useCallback, useState, type MouseEvent } from 'react'
import { Handle, Position, type NodeProps, useReactFlow } from '@xyflow/react'
import { Button } from '@/ui/button'
import { MaterialIcon } from '@/ui/material-icon'
import type { SourceNode as SourceNodeType } from '../../types/canvas.types'
import { areNodePropsEqual } from '../nodeMemo.util'
import styles from './SourceNode.module.scss'
import { cn } from '@/shared/lib/utils'
import { useAppDispatch } from '@/app/reduxHooks'
import { store } from '@/app/store'
import { openOverlay } from '@/features/adp/state/overlaySlice'
import { useAdpSessionStore } from '@/features/adp/hooks/useAdpSessionStore'
import { closeOverlayAsync } from '@/features/adp/state/overlay.thunks'
import { TruncatedSecondaryTitle } from '../TruncatedSecondaryTitle'
import NodeValidationBadge from '../../validation/NodeValidationBadge'

const SourceNode = memo(({ id, data }: NodeProps<SourceNodeType>) => {
  const [expanded, setExpanded] = useState(true)
  const { setNodes, setEdges } = useReactFlow()
  const locked = Boolean(data.locked)
  const dispatch = useAppDispatch()
  const { removeNode } = useAdpSessionStore()

  const handleSettings = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      const completedSteps = Array.isArray(data?.completed_steps) ? data.completed_steps : []
      const isConfigurationDetailsCompleted = completedSteps.includes('configuration-details')

      if (isConfigurationDetailsCompleted) {
        dispatch(
          openOverlay({
            type: 'explorer-dialog',
            data: {
              nodeId: id,
              data_source_type: data.sourceType ?? null,
            },
          })
        )
        return
      }

      if (data?.mode == null) {
        dispatch(
          openOverlay({
            type: 'source-picker',
            data: {
              nodeId: id,
              sourceType: data.sourceType,
              sourceId: data.originalId,
            },
          })
        )
      } else {
        // Will be handled in the future
      }
    },
    [data, dispatch, id]
  )

  const handleToggleExpand = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setExpanded(v => !v)
  }, [])

  const handleLock = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setNodes(nodes =>
        nodes.map(node => {
          if (node.id !== id) return node
          const nodeData = node.data as SourceNodeType['data']
          const nextLocked = !nodeData.locked
          return {
            ...node,
            draggable: !nextLocked,
            data: { ...node.data, locked: nextLocked },
          }
        })
      )
    },
    [id, setNodes]
  )

  const handleDuplicate = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setNodes(nodes => {
        const self = nodes.find(node => node.id === id)
        if (!self) return nodes
        const clone = {
          ...self,
          id: `${self.id}-copy-${Date.now()}`,
          position: { x: self.position.x + 28, y: self.position.y + 28 },
          draggable: true,
          data: { ...self.data, locked: false },
        }
        return nodes.concat(clone)
      })
    },
    [id, setNodes]
  )

  const handleDelete = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      const overlay = store.getState().overlay

      const overlayNodeId =
        overlay.data && typeof overlay.data === 'object' && 'nodeId' in overlay.data
          ? overlay.data.nodeId
          : null

      if (overlay.isOpen && overlayNodeId != null && String(overlayNodeId) === String(id)) {
        dispatch(closeOverlayAsync())
      }

      setNodes(nodes => nodes.filter(node => node.id !== id))
      setEdges(edges => edges.filter(edge => edge.source !== id && edge.target !== id))
      removeNode(id)
    },
    [dispatch, id, removeNode, setEdges, setNodes]
  )

  const titlesBlock = (
    <div className={styles.bodyText}>
      <div className={styles.bodyTitle}>{data.nodeTitle}</div>
      {data.nodeSecondaryTitle && (
        <TruncatedSecondaryTitle text={data.nodeSecondaryTitle} className={styles.bodySubtitle} />
      )}
    </div>
  )

  return (
    <div className={styles.node} data-validation={data.validation?.severity}>
      <NodeValidationBadge nodeId={id} validation={data.validation} />
      <div className={styles.cardInner}>
        {/* ── Header (shared between expanded & collapsed) ── */}
        <div className={cn(styles.header, !expanded && styles.headerCollapsed)}>
          <div className={styles.headerLeft}>
            <span className={styles.headerTitle}>Source</span>
            {expanded && (
              <Button
                variant="ghost"
                size="icon"
                className={cn(styles.headerIconBtn, 'nodrag')}
                aria-label="Settings"
                onPointerDown={e => e.stopPropagation()}
                onClick={handleSettings}
              >
                <MaterialIcon name="settings" size={16} />
              </Button>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className={cn(styles.headerIconBtn, 'nodrag')}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onPointerDown={e => e.stopPropagation()}
            onClick={handleToggleExpand}
          >
            <MaterialIcon name="unfold_more" size={18} />
          </Button>
        </div>

        {expanded ? (
          <>
            {/* ── Body ── */}
            <div className={styles.body}>
              {data.image && (
                <div className={styles.logoWell}>
                  <img src={data.image} alt={data.nodeTitle} className={styles.logoImg} />
                </div>
              )}
              {titlesBlock}
            </div>

            {/* ── Footer ── */}
            <div className={styles.footer}>
              <Button
                variant="ghost"
                size="icon"
                className={`${styles.footerBtn} nodrag`}
                aria-label={locked ? 'Unlock node' : 'Lock node'}
                aria-pressed={locked}
                onPointerDown={e => e.stopPropagation()}
                onClick={handleLock}
              >
                <MaterialIcon name={locked ? 'lock' : 'lock_open'} size={18} />
              </Button>
              <div className={styles.footerRight}>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`${styles.footerBtn} nodrag`}
                  aria-label="Duplicate"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={handleDuplicate}
                >
                  <MaterialIcon name="content_copy" size={18} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(styles.footerBtn, styles.footerDelete, 'nodrag')}
                  aria-label="Delete"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={handleDelete}
                >
                  <MaterialIcon name="delete" size={18} />
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* ── Collapsed row ── */
          <div className={styles.collapsedRow}>
            {data.image && (
              <div className={styles.logoWellSm}>
                <img src={data.image} alt={data.nodeTitle} className={styles.logoImgSm} />
              </div>
            )}
            {titlesBlock}
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className={styles.handle}
        isConnectable={!locked}
      />
    </div>
  )
}, areNodePropsEqual)

SourceNode.displayName = 'SourceNode'

export { SourceNode }
